import React, { ReactElement, ReactNode } from "react";
import { Box, CircularProgress, makeStyles } from "@material-ui/core";
import { tesaBlue, tesaGrey1, tesaGrey5, tesaWhite } from "../../styles/colors";
import FileReference from "./model/FileReference.model";
import ImageView from "./ImageView";
import Square from "./Square";
import LoadedFile from "./model/LoadedFile.model";
import UploadingFile from "./model/UploadingFile.model";
import UploadingFileView from "./UpladingFileView";
import StyledTooltip from "../Wrapper/StyledTooltip";
import { HttpErrorType } from "../../models/HttpErrorType";

const useStyles = makeStyles({
  container: {
    backgroundColor: tesaGrey5,
  },
  containerOverlay: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: tesaBlue,
    opacity: 0,
    zIndex: 10,
    "&:hover": {
      cursor: "pointer",
      opacity: 0.5,
    },
  },
  tagOverlay: {
    position: "absolute",
    height: 24,
    right: 9,
    top: 9,
    zIndex: 11,
    "&:hover": {
      cursor: "pointer",
    },
  },
  tagInlay: {
    position: "absolute",
    height: 16,
    width: 16,
    right: 4,
    top: 4,
    zIndex: -1,
    backgroundColor: tesaWhite,
  },
  tagIcon: {
    color: tesaBlue,
  },
});

export interface FileViewProps {
  file: FileReference;
  downloadImage: (id: string) => Promise<string>;
  onFileClick: (file: FileReference) => void;
  onHttpError: (error?: HttpErrorType) => void;
  shouldRenderIndicator: (file: LoadedFile) => { title: string; icon: ReactElement } | null;
}

function FileView(props: FileViewProps) {
  const { file, downloadImage, onFileClick, onHttpError, shouldRenderIndicator } = props;

  const renderContent = (fileReference: FileReference): ReactNode => {
    if (fileReference.status === "LOADING") {
      return <CircularProgress variant="indeterminate" style={{ color: tesaBlue }} />;
    }
    if (fileReference.status === "UPLOADING") {
      return <UploadingFileView progress={(fileReference as UploadingFile).progress} />;
    }
    if (fileReference.status === "LOADED") {
      const loadedFile = fileReference as LoadedFile;
      if (
        loadedFile.isImage &&
        (!loadedFile.flag ||
          loadedFile.flag.key === "picture" ||
          loadedFile.flag.key === "specification" ||
          loadedFile.flag.key === "others")
      ) {
        return <ImageView id={loadedFile.id} downloadImage={downloadImage} onHttpError={onHttpError} />;
      }
      const type =
        loadedFile.flag &&
        loadedFile.flag.key !== "others" &&
        loadedFile.flag.key !== "specification" &&
        loadedFile.flag.key !== "document"
          ? loadedFile.flag.key
          : loadedFile.type;
      return (
        <Box fontWeight={700} fontSize="36px" color={tesaGrey1}>
          {type.toUpperCase()}
        </Box>
      );
    }
  };

  const onOverlayClick = () => onFileClick(file);
  const name = file.status === "LOADING" ? "" : (file as LoadedFile | UploadingFile).name;
  const description = file.status === "LOADED" ? (file as LoadedFile).description : "";
  const indicator = file.status === "LOADED" ? shouldRenderIndicator(file as LoadedFile) : null;
  const classes = useStyles();

  return (
    <>
      <Square>
        <Box className={classes.container} height="100%" display="flex" justifyContent="center" alignItems="center">
          {renderContent(file)}
        </Box>
        <StyledTooltip title={description || ""}>
          <Box className={classes.containerOverlay} onClick={onOverlayClick} />
        </StyledTooltip>
        {indicator && (
          <StyledTooltip title={indicator.title}>
            <Box className={classes.tagOverlay} onClick={onOverlayClick}>
              <Box className={classes.tagInlay} />
              <Box className={classes.tagIcon}>{indicator.icon}</Box>
            </Box>
          </StyledTooltip>
        )}
      </Square>
      <StyledTooltip title={name}>
        <Box
          marginTop={1}
          height={24}
          lineHeight={1.5}
          style={
            name
              ? {
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }
              : { backgroundColor: tesaGrey5 }
          }
        >
          {name}
        </Box>
      </StyledTooltip>
    </>
  );
}

export default FileView;
